import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  Stack,
  useTheme,
  Text,
  DarkTooltip,
} from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  Add as AddIcon,
  InsertDriveFile as InsertDriveFileIcon,
  CampaignOutlined,
  HelpOutline,
  KeyboardTab,
  KeyboardBackspace,
} from '@mui/icons-material';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  NewChatButton,
  SidebarIcon,
  HelpModal,
  ReportUsageCard,
} from 'components/ChatBV/components';
import { BrandLogoChat } from 'components/ChatBV/components/BrandLogoChat';
import {
  CHATBV_GA4_EVENTS,
  FEEDBACK_FORM_URL,
} from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context/ChatContext';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

interface ChatPageSidebarProps {
  onRecentReportsClick?: () => void;
  onCloseRecentReports?: () => void;
}

export const ChatPageSidebar = ({
  onRecentReportsClick,
  onCloseRecentReports,
}: ChatPageSidebarProps) => {
  const theme = useTheme();
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isNewChatHovered, setIsNewChatHovered] = useState(false);
  const [isReportLibraryHovered, setIsReportLibraryHovered] = useState(false);
  const [isHelpHovered, setIsHelpHovered] = useState(false);
  const [isFeedbackHovered, setIsFeedbackHovered] = useState(false);
  const { handleNewChat } = useContext(ChatContext);
  const { trackEventGA4 } = useContext(AppConfig);
  const { isDesktop } = useScreenSize();
  const { reportsRun, reportLimit, reportsRunPercentage, isLimitedUser } =
    useLimitedPlanInfo();
  const navigate = useNavigate();

  const handleNewChatClick = () => {
    handleNewChat();
    onCloseRecentReports?.();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.new_chat,
    });
  };

  const handleToggleCollapsed = (value: boolean) => {
    if (isDesktop && value) {
      setCollapsed(value);
    } else {
      setCollapsed(false);
    }

    setCollapsed(value);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: collapsed
        ? CHATBV_GA4_EVENTS.navSidebar.subTypes.expand
        : CHATBV_GA4_EVENTS.navSidebar.subTypes.collapse,
    });
  };

  const handleHelpModalClick = () => {
    setOpenHelpModal(true);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.help_modal,
    });
  };

  const handleFeedbackFormClick = () => {
    window.open(FEEDBACK_FORM_URL, '_blank');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.feedback_form,
    });
  };

  const handleRecentReportsClick = () => {
    onRecentReportsClick?.();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: 'report_library',
    });
  };

  const handleUpgradePlanClick = () => {
    navigate('/upgrade/plan');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.upgrade_plan,
    });
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
        }}
      >
        <Stack
          height="100%"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            p: 2,
          }}
        >
          <Stack spacing={4} paddingTop={6} alignItems="center">
            <DarkTooltip title={collapsed ? 'Expand' : ''}>
              <SidebarIcon>
                {collapsed ? (
                  <KeyboardTab onClick={() => handleToggleCollapsed(false)} />
                ) : (
                  <KeyboardBackspace
                    onClick={() => handleToggleCollapsed(true)}
                  />
                )}
              </SidebarIcon>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'New Chat' : ''}>
              <NewChatButton
                variant="contained"
                onClick={handleNewChatClick}
                onMouseEnter={() => setIsNewChatHovered(true)}
                onMouseLeave={() => setIsNewChatHovered(false)}
                sx={{
                  backgroundColor: isNewChatHovered
                    ? theme.palette.button.dark
                    : theme.palette.primary.main,
                }}
              >
                <AddIcon
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: theme.palette.text.contrastText,
                  }}
                />
              </NewChatButton>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'Report Library' : ''}>
              <SidebarIcon
                onClick={handleRecentReportsClick}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
                onMouseEnter={() => setIsReportLibraryHovered(true)}
                onMouseLeave={() => setIsReportLibraryHovered(false)}
              >
                <InsertDriveFileIcon
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: isReportLibraryHovered
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  }}
                />
              </SidebarIcon>
            </DarkTooltip>
          </Stack>
          <Stack spacing={2} alignItems="center" paddingBottom={4}>
            <DarkTooltip title={collapsed ? 'Help' : ''}>
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                sx={{
                  width: '100%',
                  cursor: 'pointer',
                }}
                onClick={handleHelpModalClick}
              >
                <SidebarIcon
                  sx={{
                    color: isHelpHovered
                      ? theme.palette.primary.main
                      : 'inherit',
                  }}
                  onMouseEnter={() => setIsHelpHovered(true)}
                  onMouseLeave={() => setIsHelpHovered(false)}
                >
                  <HelpOutline
                    sx={{
                      color: isHelpHovered
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    }}
                  />
                </SidebarIcon>
              </Box>
            </DarkTooltip>
            <DarkTooltip title={collapsed ? 'Feedback' : ''}>
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                sx={{
                  width: '100%',
                  cursor: 'pointer',
                }}
                onClick={handleFeedbackFormClick}
              >
                <SidebarIcon
                  sx={{
                    color: isFeedbackHovered
                      ? theme.palette.primary.main
                      : 'inherit',
                  }}
                  onMouseEnter={() => setIsFeedbackHovered(true)}
                  onMouseLeave={() => setIsFeedbackHovered(false)}
                >
                  <CampaignOutlined
                    sx={{
                      color: isFeedbackHovered
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    }}
                  />
                </SidebarIcon>
              </Box>
            </DarkTooltip>
          </Stack>
        </Stack>
        <Stack
          height="100%"
          justifyContent="space-between"
          sx={{
            py: 2,
            transition: 'width 0.3s ease-in-out',
            transitionDelay: collapsed ? '0.1s' : '0',
          }}
          width={collapsed ? '0px' : '150px'}
        >
          <Stack
            spacing={3}
            paddingTop={5}
            sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
              transitionDelay: collapsed ? '0' : '0.2s',
            }}
          >
            <BrandLogoChat width="108px" height="40px" />
            <Text
              variant="body1"
              sx={{
                fontWeight: 'bold',
                textWrap: 'nowrap',
                color: isNewChatHovered
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
                marginTop: '25px !important',
              }}
              onClick={handleNewChatClick}
              onMouseEnter={() => setIsNewChatHovered(true)}
              onMouseLeave={() => setIsNewChatHovered(false)}
            >
              New Chat
            </Text>
            <Text
              variant="body1"
              sx={{
                fontWeight: 'bold',
                textWrap: 'nowrap',
                color: isReportLibraryHovered
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
                marginTop: '39px !important',
              }}
              onClick={handleRecentReportsClick}
              onMouseEnter={() => setIsReportLibraryHovered(true)}
              onMouseLeave={() => setIsReportLibraryHovered(false)}
            >
              Report Library
            </Text>
          </Stack>
          <Stack
            spacing={2}
            paddingBottom={3.75}
            sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
              transitionDelay: collapsed ? '0s' : '0.2s',
            }}
          >
            {isLimitedUser && (
              <ReportUsageCard
                reportsRun={reportsRun}
                reportsLimit={reportLimit}
                progressValue={reportsRunPercentage}
                sx={{
                  marginLeft: `-48px !important`,
                  marginRight: '8px !important',
                }}
                onUpgradeClick={handleUpgradePlanClick}
              />
            )}

            <Text
              onClick={handleHelpModalClick}
              variant="body1"
              sx={{
                cursor: 'pointer',
                color: isHelpHovered ? theme.palette.primary.main : 'inherit',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
              }}
              onMouseEnter={() => setIsHelpHovered(true)}
              onMouseLeave={() => setIsHelpHovered(false)}
            >
              Help
            </Text>
            <Text
              onClick={handleFeedbackFormClick}
              variant="body1"
              sx={{
                cursor: 'pointer',
                color: isFeedbackHovered
                  ? theme.palette.primary.main
                  : 'inherit',
                '&:hover': {
                  color: theme.palette.primary.main,
                },
                marginTop: '18px !important',
              }}
              onMouseEnter={() => setIsFeedbackHovered(true)}
              onMouseLeave={() => setIsFeedbackHovered(false)}
            >
              Feedback
            </Text>
          </Stack>
        </Stack>
      </Box>
      <HelpModal open={openHelpModal} onClose={() => setOpenHelpModal(false)} />
    </>
  );
};
